/**
 * 统计图表：纯 CSS 条形图（无第三方图表依赖），数据来自 StatsResult。
 */
import type { StatsResult } from '../lib/types';
import { PRIORITY_LABELS, formatMinutes } from '../lib/utils';
import { Card, SectionTitle, StatCard } from './ui';

interface StatsChartsProps {
  stats: StatsResult;
}

function BarRow({ label, done, total }: { label: string; done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        <span className="text-slate-400">
          {done}/{total} · {pct}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function StatsCharts({ stats }: StatsChartsProps) {
  return (
    <div className="space-y-6">
      {/* 概览 */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="任务总数" value={stats.total} accent="indigo" />
        <StatCard label="已完成" value={stats.done} accent="emerald" />
        <StatCard label="已逾期" value={stats.overdue} accent="rose" />
        <StatCard
          label="完成率"
          value={`${Math.round(stats.completionRate * 100)}%`}
          sub={`专注 ${formatMinutes(stats.focusMinutesTotal)}`}
          accent="sky"
        />
      </div>

      {/* 按领域 */}
      <Card className="p-5">
        <SectionTitle title="按领域" />
        <div className="space-y-3">
          {stats.byArea.length === 0 && <p className="text-sm text-slate-400">暂无数据</p>}
          {stats.byArea.map((a) => (
            <BarRow key={a.areaId} label={a.name ?? '未分类'} done={a.done} total={a.total} />
          ))}
        </div>
      </Card>

      {/* 按优先级 */}
      <Card className="p-5">
        <SectionTitle title="按优先级" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((p) => {
            const row = stats.byPriority.find((x) => x.priority === p);
            return <BarRow key={p} label={PRIORITY_LABELS[p as 1 | 2 | 3 | 4]} done={row?.done ?? 0} total={row?.total ?? 0} />;
          })}
        </div>
      </Card>

      {/* 习惯连续天数 */}
      <Card className="p-5">
        <SectionTitle title="习惯连续天数" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {stats.habitStreaks.length === 0 && <p className="text-sm text-slate-400">暂无习惯</p>}
          {stats.habitStreaks.map((h) => (
            <div key={h.habitId} className="rounded-lg bg-slate-50 p-3 text-center dark:bg-slate-800/60">
              <div className="text-2xl font-semibold text-amber-500">{h.streak}</div>
              <div className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">{h.name}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
